// src/components/Login.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Link,
  Divider,
  styled,
  alpha,
  TextField,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import previewImage from '../assets/preview.png';
import logo from '../assets/logo.png';

// ─────────────────────────────────────────────────────────────
// STYLED COMPONENTS
// ─────────────────────────────────────────────────────────────
const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#ffffff',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 40%',
  padding: theme.spacing(6),
  backgroundColor: '#fff',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    // Once the illustration panel is hidden this becomes the whole screen, so
    // cap and centre the form rather than letting it stretch edge to edge.
    flex: '1 1 auto',
    width: '100%',
    maxWidth: 440,
    margin: '0 auto',
    padding: theme.spacing(4, 3),
    justifyContent: 'center',
    minHeight: '100vh',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 2.5),
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  flex: '1 1 auto',
  backgroundColor: '#f7f8fa',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const BrandHeader = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 600,
  marginBottom: theme.spacing(4),
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: 28,
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    fontSize: 24,
  },
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: '#6b7280',
  marginBottom: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
  },
  '& input:-webkit-autofill': {
    WebkitBoxShadow: `0 0 0 1000px #fff inset !important`,
    boxShadow: `0 0 0 1000px #fff inset !important`,
  },
}));

const BlackButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: 12,
  padding: theme.spacing(1.5),
  minHeight: 48,
  fontWeight: 600,
  backgroundColor: '#000',
  color: '#fff',
  '&:hover': {
    backgroundColor: alpha('#000', 0.85),
  },
  '&.Mui-disabled': {
    backgroundColor: '#000', // keep black background
    color: '#fff',           // keep white text visible
    opacity: 0.7,            // slightly dim to show disabled state
  },
}));

const WhiteButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: 12,
  padding: theme.spacing(1.5),
  minHeight: 48,
  fontWeight: 600,
  backgroundColor: '#fff',
  border: '1px solid #e5e7eb',
  color: '#374151',
  '&:hover': {
    backgroundColor: '#f9fafb',
  },
}));

// ─────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────
export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (cooldown > 0 || isSubmitting) return;

    if (!username.trim() || !password.trim()) {
      setError('Please enter your email and password.');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: username.toLowerCase().trim(), password }),
      });

      const data = await res.json();

      // Valid credentials but no active membership — send them to finish paying.
      if (res.status === 402 && data.code === 'subscription_required' && data.token) {
        localStorage.setItem('token', data.token);
        window.location.href = `/subscribe/${data.vendor._id}`;
        return;
      }

      if (!res.ok) {
        setError(data.message || 'Invalid email or password.');
        setCooldown(3);
        setIsSubmitting(false);
        return;
      }

      localStorage.setItem('token', data.token);
      window.location.href = `/dashboard/${data.vendor._id}`;
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again later.');
      setCooldown(3);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = () => {
    window.location.href = `/register`;
  };

  return (
    <Container>
      {/* LEFT PANEL */}
      <LeftPanel>
        <Box component="a" href="/" sx={{ display: 'inline-block', mb: 4, textDecoration: 'none' }}>
          <Box component="img" src={logo} alt="Rewarding" sx={{ height: 32 }} />
        </Box>

        <Title>Sign in</Title>
        <SubTitle>Access all your Rewarding data here.</SubTitle>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSignIn}>
          <StyledTextField
            label="Email Address"
            variant="outlined"
            fullWidth
            type="email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <StyledTextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small">
                    {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <BlackButton
            type="submit"
            fullWidth
            disabled={isSubmitting || cooldown > 0}
            sx={{ mt: 2, mb: 2 }}
          >
            {cooldown > 0 ? `Try again in ${cooldown}s` : 'Sign In'}
          </BlackButton>
        </form>

        <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
          <Divider sx={{ flex: 1 }} />
          <Typography variant="body2" sx={{ mx: 2, color: '#9ca3af' }}>
            or
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Box>

        <WhiteButton fullWidth onClick={handleRegister}>
          Register!
        </WhiteButton>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Link
            component="button"
            type="button"
            underline="hover"
            sx={{ fontSize: 14 }}
            onClick={() => { window.location.href = '/forgot-password'; }}
          >
            Forgot password?
          </Link>
        </Box>
      </LeftPanel>

      <RightPanel sx={{ flexDirection: 'column', rowGap: 4 }}>
        <Box sx={{ marginRight: '80%', display: 'flex', columnGap: 0.8 }}>
          <Box sx={{ borderRadius: '100%', width: 10, height: 10, backgroundColor: '#FF5F57' }} />
          <Box sx={{ borderRadius: '100%', width: 10, height: 10, backgroundColor: '#FFBD2E' }} />
          <Box sx={{ borderRadius: '100%', width: 10, height: 10, backgroundColor: '#27C93F' }} />
        </Box>

        <Box
          component="img"
          src={previewImage}
          alt="Website Preview"
          sx={{ width: '130%', height: 'auto', marginRight: '-45%' }}
        />
      </RightPanel>
    </Container>
  );
}
